<template>
  <div class="audio-player" :class="{ 'is-loading': loading, 'is-error': error }">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>
    
    <!-- 错误状态 -->
    <div v-else-if="error" class="error-state">
      <i class="el-icon-warning"></i>
      <span>{{ errorMessage }}</span>
      <el-button type="text" size="small" @click="reload">重试</el-button>
    </div>
    
    <!-- 播放器主体 -->
    <div v-else class="player-body">
      <!-- 可视化波形 -->
      <div class="visualizer-bars">
        <span v-for="n in 30" :key="n" class="bar" :class="{ active: isPlaying }" :style="{ animationDelay: (n * 0.08) + 's' }"></span>
      </div>
      
      <!-- 进度条区域 -->
      <div class="progress-section">
        <el-slider
          v-model="progressPercent"
          :show-tooltip="false"
          class="progress-slider"
          @change="handleSeek"
          @input="handleSeekInput"
        />
        <div class="time-labels">
          <span class="time current">{{ formatTime(currentTime) }}</span>
          <span class="time duration">{{ formatTime(duration) }}</span>
        </div>
      </div>
      
      <!-- 控制区域 -->
      <div class="controls">
        <!-- 播放速度 -->
        <el-dropdown trigger="click" @command="handleSpeedChange" size="small">
          <span class="speed-btn">{{ playbackRate }}x</span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item :command="0.5">0.5x</el-dropdown-item>
            <el-dropdown-item :command="0.75">0.75x</el-dropdown-item>
            <el-dropdown-item :command="1">1x</el-dropdown-item>
            <el-dropdown-item :command="1.25">1.25x</el-dropdown-item>
            <el-dropdown-item :command="1.5">1.5x</el-dropdown-item>
            <el-dropdown-item :command="2">2x</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        
        <!-- 音量 -->
        <el-popover placement="top" trigger="click" width="40">
          <el-slider v-model="volume" vertical height="80px" :show-tooltip="false" @input="handleVolumeChange" />
          <i slot="reference" class="ctrl-btn" :class="volumeIcon"></i>
        </el-popover>
        
        <!-- 快退 -->
        <i class="el-icon-refresh-left ctrl-btn" @click="seekRelative(-5)" title="快退5秒"></i>
        
        <!-- 播放按钮 -->
        <div class="play-btn" @click="togglePlay">
          <i :class="isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
        </div>
        
        <!-- 快进 -->
        <i class="el-icon-refresh-right ctrl-btn" @click="seekRelative(5)" title="快进5秒"></i>
        
        <!-- 下载 -->
        <i class="el-icon-download ctrl-btn" @click="handleDownload" title="下载录音"></i>
      </div>
    </div>
    
    <!-- 隐藏的 audio 元素 -->
    <audio
      ref="audio"
      :src="src"
      preload="auto"
      @loadstart="onLoadStart"
      @loadedmetadata="onLoadedMetadata"
      @durationchange="onDurationChange"
      @canplay="onCanPlay"
      @timeupdate="onTimeUpdate"
      @ended="onEnded"
      @error="onError"
      @waiting="onWaiting"
      @playing="onPlaying"
    ></audio>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  props: {
    src: {
      type: String,
      required: true
    },
    filename: {
      type: String,
      default: '录音文件'
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '加载失败',
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      progressPercent: 0,
      volume: 80,
      playbackRate: 1,
      isSeeking: false
    }
  },
  computed: {
    volumeIcon() {
      if (this.volume === 0) return 'el-icon-turn-off-microphone'
      if (this.volume < 50) return 'el-icon-microphone'
      return 'el-icon-microphone'
    }
  },
  watch: {
    src: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loading = true
          this.error = false
          this.currentTime = 0
          this.progressPercent = 0
          this.duration = 0
          // 使用 AudioContext 预加载时长
          this.fetchAudioDuration(newVal)
        }
      }
    }
  },
  methods: {
    // 使用临时 Audio 对象获取时长
    fetchAudioDuration(url) {
      const tempAudio = new Audio()
      tempAudio.preload = 'metadata'
      
      tempAudio.addEventListener('loadedmetadata', () => {
        if (tempAudio.duration && isFinite(tempAudio.duration)) {
          this.duration = tempAudio.duration
        }
      })
      
      // 尝试通过 seek 到末尾来获取时长
      tempAudio.addEventListener('durationchange', () => {
        if (tempAudio.duration && isFinite(tempAudio.duration)) {
          this.duration = tempAudio.duration
        }
      })
      
      // tempAudio 加载失败时忽略（主 audio 元素会处理错误）
      tempAudio.addEventListener('error', () => {
        // 不处理，让主 audio 元素的 onError 来显示错误
      })
      
      tempAudio.src = url
      
      // 超时 5 秒自动退出加载状态
      setTimeout(() => {
        if (this.loading) {
          this.loading = false
        }
      }, 5000)
    },
    
    // 格式化时间 (秒 -> mm:ss)
    formatTime(seconds) {
      if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    
    // 播放/暂停切换
    togglePlay() {
      const audio = this.$refs.audio
      if (!audio) return
      
      if (this.isPlaying) {
        audio.pause()
      } else {
        // 尝试播放，如果失败则尝试移除crossorigin后重试
        audio.play().then(() => {
          console.log('[AudioPlayer] 播放成功')
        }).catch(err => {
          console.error('[AudioPlayer] 播放失败:', err)
          // 如果是CORS错误，尝试移除crossorigin后重试
          if (err.name === 'NotAllowedError' || err.name === 'NotSupportedError') {
            console.log('[AudioPlayer] 尝试移除crossorigin后重试...')
            audio.removeAttribute('crossorigin')
            audio.load()
            setTimeout(() => {
              audio.play().catch(e => {
                console.error('[AudioPlayer] 重试播放失败:', e)
                this.$message.error('播放失败，请重试')
              })
            }, 500)
          } else {
            this.$message.error('播放失败，请重试')
          }
        })
      }
    },
    
    // 进度条拖动中
    handleSeekInput(val) {
      this.isSeeking = true
      const newTime = (val / 100) * this.duration
      this.currentTime = newTime
    },
    
    // 进度条拖动完成
    handleSeek(val) {
      const audio = this.$refs.audio
      if (!audio || !this.duration) return
      
      const newTime = (val / 100) * this.duration
      audio.currentTime = newTime
      this.isSeeking = false
    },
    
    // 快进/快退
    seekRelative(seconds) {
      const audio = this.$refs.audio
      if (!audio) return
      const newTime = Math.max(0, Math.min(this.duration, audio.currentTime + seconds))
      audio.currentTime = newTime
    },
    
    // 播放速度切换
    handleSpeedChange(rate) {
      this.playbackRate = rate
      const audio = this.$refs.audio
      if (audio) {
        audio.playbackRate = rate
      }
    },
    
    // 音量调整
    handleVolumeChange(val) {
      const audio = this.$refs.audio
      if (audio) {
        audio.volume = val / 100
      }
    },
    
    // 下载录音
    async handleDownload() {
      if (!this.src) return
      
      try {
        this.$message.info('正在下载...')
        
        const response = await fetch(this.src)
        const blob = await response.blob()
        
        // 获取文件扩展名
        let ext = 'mp3'
        const urlPath = this.src.split('?')[0]
        const match = urlPath.match(/\.(\w+)$/)
        if (match) ext = match[1]
        
        // 创建下载链接
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.filename}.${ext}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$message.success('下载成功')
      } catch (err) {
        console.error('下载失败:', err)
        // 降级方案：直接打开链接
        window.open(this.src, '_blank')
      }
    },
    
    // 重新加载
    reload() {
      this.loading = true
      this.error = false
      const audio = this.$refs.audio
      if (audio) {
        audio.load()
      }
    },
    
    // 音频事件处理
    onLoadStart() {
      this.loading = true
    },
    
    onLoadedMetadata() {
      const audio = this.$refs.audio
      if (audio) {
        this.duration = audio.duration
      }
    },
    
    onCanPlay() {
      this.loading = false
      this.error = false
    },
    
    onDurationChange() {
      const audio = this.$refs.audio
      if (audio && audio.duration && isFinite(audio.duration)) {
        this.duration = audio.duration
      }
    },
    
    onTimeUpdate() {
      if (this.isSeeking) return
      
      const audio = this.$refs.audio
      if (audio) {
        this.currentTime = audio.currentTime
        
        // 网络音频可能在播放过程中才能获取到时长
        if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
          if (this.duration !== audio.duration) {
            this.duration = audio.duration
          }
          this.progressPercent = (audio.currentTime / this.duration) * 100
        }
      }
    },
    
    onEnded() {
      this.isPlaying = false
      this.progressPercent = 0
      this.currentTime = 0
      this.stopProgressTimer()
    },
    
    onError(e) {
      console.error('音频加载错误:', e)
      this.loading = false
      this.error = true
      this.errorMessage = '音频加载失败'
    },
    
    onWaiting() {
      // 缓冲中
    },
    
    onPlaying() {
      this.isPlaying = true
      this.loading = false
      this.startProgressTimer()
    },
    
    // 定时轮询进度（fallback）
    startProgressTimer() {
      this.stopProgressTimer()
      this._progressTimer = setInterval(() => {
        const audio = this.$refs.audio
        if (!audio) return
        if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
          this.currentTime = audio.currentTime
          this.duration = audio.duration
          this.progressPercent = (audio.currentTime / audio.duration) * 100
        }
      }, 200)
    },
    stopProgressTimer() {
      if (this._progressTimer) {
        clearInterval(this._progressTimer)
        this._progressTimer = null
      }
    }
  },
  mounted() {
    const audio = this.$refs.audio
    if (audio) {
      audio.volume = this.volume / 100
      
      // 监听暂停事件
      audio.addEventListener('pause', () => {
        this.isPlaying = false
        this.stopProgressTimer()
      })
    }
  },
  beforeDestroy() {
    this.stopProgressTimer()
    const audio = this.$refs.audio
    if (audio) {
      audio.pause()
    }
  }
}
</script>

<style lang="scss" scoped>
.audio-player {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  
  &.is-loading, &.is-error {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 80px;
  }
}

.loading-state, .error-state {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 13px;
  
  i { font-size: 18px; }
  
  .el-button {
    color: #3b82f6;
    margin-left: 8px;
  }
}

.player-body {
  display: flex;
  flex-direction: column;
}

/* 波形可视化 */
.visualizer-bars {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 56px;
  margin-bottom: 16px;
  padding: 0 4px;
  
  .bar {
    flex: 1;
    max-width: 8px;
    background: #3b82f6;
    border-radius: 4px;
    height: 16px;
    
    /* 模拟波形高度和透明度变化 */
    &:nth-child(1)  { height: 14px; opacity: 0.35; }
    &:nth-child(2)  { height: 20px; opacity: 0.4; }
    &:nth-child(3)  { height: 10px; opacity: 0.35; }
    &:nth-child(4)  { height: 28px; opacity: 0.5; }
    &:nth-child(5)  { height: 18px; opacity: 0.45; }
    &:nth-child(6)  { height: 34px; opacity: 0.6; }
    &:nth-child(7)  { height: 22px; opacity: 0.55; }
    &:nth-child(8)  { height: 40px; opacity: 0.7; }
    &:nth-child(9)  { height: 30px; opacity: 0.65; }
    &:nth-child(10) { height: 46px; opacity: 0.8; }
    &:nth-child(11) { height: 38px; opacity: 0.75; }
    &:nth-child(12) { height: 52px; opacity: 0.9; }
    &:nth-child(13) { height: 44px; opacity: 0.85; }
    &:nth-child(14) { height: 56px; opacity: 1; }
    &:nth-child(15) { height: 48px; opacity: 0.9; }
    &:nth-child(16) { height: 12px; opacity: 0.3; }
    &:nth-child(17) { height: 8px;  opacity: 0.3; }
    &:nth-child(18) { height: 16px; opacity: 0.4; }
    &:nth-child(19) { height: 36px; opacity: 0.65; }
    &:nth-child(20) { height: 28px; opacity: 0.55; }
    &:nth-child(21) { height: 44px; opacity: 0.8; }
    &:nth-child(22) { height: 50px; opacity: 0.9; }
    &:nth-child(23) { height: 38px; opacity: 0.7; }
    &:nth-child(24) { height: 52px; opacity: 0.95; }
    &:nth-child(25) { height: 42px; opacity: 0.8; }
    &:nth-child(26) { height: 32px; opacity: 0.6; }
    &:nth-child(27) { height: 24px; opacity: 0.5; }
    &:nth-child(28) { height: 18px; opacity: 0.4; }
    &:nth-child(29) { height: 26px; opacity: 0.5; }
    &:nth-child(30) { height: 14px; opacity: 0.35; }
    
    &.active {
      animation: barPulse 0.8s ease-in-out infinite alternate;
    }
  }
}

@keyframes barPulse {
  0% { opacity: 0.3; transform: scaleY(0.5); }
  100% { opacity: 1; transform: scaleY(1); }
}

/* 进度条区域 */
.progress-section {
  margin-bottom: 16px;
  
  .progress-slider {
    ::v-deep .el-slider__runway {
      height: 4px;
      background: #e2e8f0;
      border-radius: 2px;
    }
    
    ::v-deep .el-slider__bar {
      height: 4px;
      background: #3b82f6;
      border-radius: 2px;
    }
    
    ::v-deep .el-slider__button-wrapper {
      top: -12px;
    }
    
    ::v-deep .el-slider__button {
      width: 12px;
      height: 12px;
      border: 2px solid #3b82f6;
      background: #fff;
    }
  }
  
  .time-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
  }
  
  .time {
    color: #94a3b8;
    font-size: 12px;
    font-family: 'Roboto Mono', monospace;
  }
}

/* 控制区域 */
.controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.speed-btn {
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  transition: all 0.2s;
  font-weight: 500;
  
  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }
}

.ctrl-btn {
  color: #64748b;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: #3b82f6;
    transform: scale(1.1);
  }
}

.play-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  
  &:hover {
    background: #2563eb;
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  }
  
  i {
    color: #fff;
    font-size: 24px;
  }
}
</style>
